import {
  Component,
  OnInit,
  Input,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { DataSet } from '../models';
import { ChartsVisualization } from '@lfx/core/models';
import Chart from 'chart.js';
import 'chartjs-plugin-datalabels/dist/chartjs-plugin-datalabels.js';
import { verticalLinePlugin } from '@config';

Chart.defaults.global.plugins.datalabels.display = false;

@Component({
  selector: 'lfx-bar-chart',
  templateUrl: './bar-chart.component.html',
})
export class BarChartComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() chartColors: string[];
  @Input('visualizationData')
  set visualizationData(visualization: ChartsVisualization) {
    this.dataSets = visualization.dataSets;
    this.xAxesLabels = visualization.labels;
  }

  @Input() fill: boolean;
  @ViewChild('chartCanvas', { static: false }) canvas: ElementRef;
  @Input() options = {};
  @Input() chartType = 'bar';
  @Input() verticalAnnotations = [];

  dataSets: DataSet[] = [];
  xAxesLabels: (string | number)[] = [];
  chart: Chart;
  defaultOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
  };

  borderWidth = '1';

  /**
   * colors to Use must be HEX to support the hexRoRgb method
   */
  private fillColors = [
    '#D32F2F',
    '536DFE',
    '#FFA000',
    '#4CAF50',
    '#795548',
    '#0288D1',
  ];

  private borderColors = [
    '#D32F2F',
    '536DFE',
    '#FFA000',
    '#4CAF50',
    '#795548',
    '#0288D1',
  ];

  constructor() {
    Chart.plugins.register(verticalLinePlugin);
  }

  ngOnInit() {
    this.defaultOptions = Object.assign({}, this.defaultOptions, this.options);
  }

  ngOnChanges(): void {
    if (this.chart) {
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.createChart();
    });
  }

  private createChart() {
    const canvas = this.canvas.nativeElement.getContext('2d');
    const datasets = this.dataSets.map((dataSet, index) =>
      Object.assign({}, this.createExtraSettingsForDataSet(index), dataSet)
    );

    this.chart = new Chart(canvas, {
      type: this.chartType,
      data: {
        labels: this.xAxesLabels,
        datasets,
      },
      options: this.defaultOptions,
      lines: this.verticalAnnotations,
    });
  }

  private updateChart() {
    this.chart.data = {
      labels: this.xAxesLabels,
      datasets: this.dataSets.map((dataSet, index) =>
        Object.assign({}, this.createExtraSettingsForDataSet(index), dataSet)
      ),
    };
    this.chart.update();
  }

  private hexToRgb(hex, opacity = 1) {
    hex = hex || this.addColor();
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (!result) {
      return hex;
    }

    return (
      'rgba( ' +
      parseInt(result[1], 16) +
      ',' +
      parseInt(result[2], 16) +
      ',' +
      parseInt(result[3], 16) +
      ',' +
      opacity +
      ')'
    );
  }

  private generateRandomColor() {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
  }

  private addColor() {
    const newColor = this.generateRandomColor();

    this.fillColors.push(newColor);
    this.borderColors.push(newColor);

    return newColor;
  }

  private createExtraSettingsForDataSet(index: number): any {
    return {
      borderColor: this.borderColors[index],
      backgroundColor: this.hexToRgb(this.fillColors[index], 0.2),
      fill: this.fill ? 'origin' : false,
      borderWidth: this.borderWidth,
      datalabels: {
        display: false,
      },
    };
  }
}
